import { useEffect, useMemo } from 'react'
import currency from 'currency.js'

import { StripeProduct } from '@/actions/settings/billingActions'
import { StripeInterval } from '@/reducers/subscription.slice'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'
import { fetchCatchUpBKCoupon } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { FREE_TRIAL_DAYS, FREE_TRIAL_PROMO } from '../helpers'
import { getProductByInterval, productPriceAsCurrency } from './helpers'
import { useCatchupBookkeepingPrice } from './hooks'
import { CatchupBookkeepingSection } from './CatchupBookkeepingSection'
import { PlanDetailsSection } from './PlanDetailsSection'
import { TextColors, TypographyStyles } from '@/styles'
import { Card, Separator, Text, Toggle } from '@/ui'
import { useAnalyticsTrack } from '@/v2-features/amplitude'

export const StripeProductCard = ({
  stripeProducts,
  selectedProduct,
  onSelect,
  freeTrialEnabled,
  basicPlanEnabled,
  isLateJoiner,
  freeTrialSearchParamValue,
  basicPlanSearchParamValue,
}: {
  stripeProducts: StripeProduct[]
  selectedProduct?: StripeProduct
  onSelect: (val: StripeProduct) => void
  freeTrialEnabled: boolean
  basicPlanEnabled: boolean
  isLateJoiner: boolean | null | undefined
  freeTrialSearchParamValue: string | null | undefined
  basicPlanSearchParamValue: string | null | undefined
}) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const annualPlan = getProductByInterval(stripeProducts, StripeInterval.year)
  const monthlyPlan = getProductByInterval(stripeProducts, StripeInterval.month)
  const isAnnual = selectedProduct !== monthlyPlan
  const catchUpBKCoupon = useFetchResponse(
    fetchCatchUpBKCoupon,
    isAnnual,
    basicPlanEnabled
  )
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const track = useAnalyticsTrack()

  useEffect(() => {
    if (selectedProduct?.metadata.tier) {
      track(
        `viewed ${selectedProduct?.metadata.tier}, ${isAnnual ? 'Annual Plan' : 'Monthly Plan'} option in choose-plan`
      )
    }
  }, [track, isAnnual, selectedProduct?.metadata.tier])

  useEffect(() => {
    if (annualPlan) onSelect(annualPlan)
  }, [onSelect, annualPlan])

  const productPrice = productPriceAsCurrency({
    hasAnnualCommitment,
    product: selectedProduct,
  })

  const totalDueToday = useMemo(() => {
    if (freeTrialEnabled) {
      return currency(0)
    }

    if (catchupBookkeepingPrice) {
      return productPrice.add(
        currency(catchupBookkeepingPrice.amount).subtract(
          currency(catchupBookkeepingPrice.amount).multiply(
            (catchUpBKCoupon?.percent_off ?? 0) / 100
          )
        )
      )
    }

    return productPrice
  }, [freeTrialEnabled, catchupBookkeepingPrice, productPrice, catchUpBKCoupon])

  const subText = useMemo(() => {
    if (freeTrialSearchParamValue === FREE_TRIAL_PROMO && freeTrialEnabled) {
      return `If you choose to purchase Heard after your free trial, your plan will cost ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} ${
        isAnnual ? 'annually' : 'monthly'
      }`
    } else if (freeTrialEnabled) {
      return `${FREE_TRIAL_DAYS} days free then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }${catchupBookkeepingPrice ? ` plus a one-time ${formatCurrency(catchupBookkeepingPrice.amount, { hideDecimalsIfZero: true })} fee for catch up bookkeeping` : ''}`
    } else if (catchupBookkeepingPrice) {
      return `Then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }`
    }

    return `Billed ${isAnnual ? 'annually' : 'monthly'}`
  }, [
    freeTrialEnabled,
    freeTrialSearchParamValue,
    catchupBookkeepingPrice,
    isAnnual,
    productPrice,
  ])

  const handlePlanSelection = () => {
    if (isAnnual && monthlyPlan) {
      onSelect(monthlyPlan)
    } else if (!isAnnual && annualPlan) {
      onSelect(annualPlan)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2">
        <Toggle
          className="p-3"
          variant="outline"
          checked={isAnnual}
          onCheckedChange={() => handlePlanSelection()}
        >
          <Text>
            <b>{hasAnnualCommitment ? 'Annual Payment' : 'Annual Plan'}</b>
          </Text>
          {!hasAnnualCommitment && (
            <Text className="text-green-50">Best Value</Text>
          )}
        </Toggle>
        <Toggle
          className="p-3"
          variant="outline"
          checked={!isAnnual}
          onCheckedChange={() => handlePlanSelection()}
        >
          <Text>
            <b>{hasAnnualCommitment ? 'Monthly Payment' : 'Monthly Plan'}</b>
          </Text>
        </Toggle>
      </div>

      <Card className={`${isMobile ? 'px-4' : ''}`}>
        <PlanDetailsSection
          stripeProducts={stripeProducts}
          selectedProduct={selectedProduct}
          freeTrialEnabled={freeTrialEnabled}
          basicPlanEnabled={basicPlanEnabled}
          basicPlanSearchParamValue={basicPlanSearchParamValue}
          isLateJoiner={isLateJoiner}
        />

        <CatchupBookkeepingSection
          selectedAnnualPlan={isAnnual}
          isBasicPlanType={basicPlanEnabled}
        />

        <Separator />

        <div className="flex flex-col gap-4">
          <div className="flex items-center">
            <Text
              className="w-1/2"
              variant={isMobile ? TypographyStyles.H3 : TypographyStyles.H2}
            >
              Total Due Today
            </Text>

            <div className="w-1/2 text-right">
              <Text
                variant={
                  isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_MD
                }
                className="inline"
              >
                {formatCurrency(totalDueToday, {
                  hideDecimalsIfZero: true,
                })}
              </Text>

              {!catchupBookkeepingPrice && (
                <Text
                  variant={isMobile ? TypographyStyles.H2 : TypographyStyles.H1}
                  className="inline"
                >
                  /{isAnnual ? 'yr' : 'mo'}
                </Text>
              )}
            </div>
          </div>

          <Text
            className="min-h-fit text-right"
            color={TextColors.BRAND}
            variant={TypographyStyles.BODY_LG}
          >
            {subText}
          </Text>
        </div>
      </Card>
    </div>
  )
}
